import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, OnChanges, ViewChild } from '@angular/core';
import { StudentService } from '../../student/student.service';
import { Student } from '../../student/student.model';
import { UserService } from '../../user/user.service';
import { GeneralPreferences } from '../../user-preferences/general-preferences.model';
import { UserPreferencesService } from '../../user-preferences/user-preferences.service';
import { Subscription } from 'rxjs';
import { RegistrationStudent } from '../../../admin360/registration/models/registration-student.model';
import * as moment from 'moment';
import { DropDownListComponent } from '@progress/kendo-angular-dropdowns';

@Component({
    selector: 'studentSelection',
    templateUrl: './student-selection.component.html',
    styleUrls: ['./student-selection.component.css']
})
export class StudentSelectionComponent implements OnInit, OnDestroy, OnChanges {

    // Interface Properties
    @Input() forStudentSearch: boolean;
    @Input() includeAllOption: boolean;
    @Input() studentIndex = 0;
    @Input() showActionMenu = false;
    @Input() overrideSort: boolean;
    @Input() overrideStudent: boolean;
    @Input() showNumber: boolean;
    @Input() showEnrollmentDates = false;
    @Output() studentChanged: EventEmitter<number> = new EventEmitter<number>();
    @ViewChild('dropdownlistRef') public dropdownlist: DropDownListComponent;
    // Students
    private _students: Student[];
    @Input() selectedStudent: Student | RegistrationStudent;
    previousSelectedStudent: Student;

    // Other
    subscriptions: Subscription[] = [];
    generalPreferences: GeneralPreferences;
    studentdropDownWidth = 200;

    public virtual: any = {
        itemHeight: 30,
        pageSize: 15
    };

    // Class Functions
    constructor(
        private studentService: StudentService,
        private userService: UserService,
        private userPreferencesService: UserPreferencesService,
    ) {
    }

    get userType(): number {
        return this.userService.currentUser.UserType;
    }

    get students(): Student[] {
        return this._students;
    }

    @Input() set students(value: Student[]) {
        this._students = JSON.parse(JSON.stringify(value));
        this.getStudentLabels();
        if (this.userType == 1 && this.generalPreferences && !this.overrideSort) {
            this.sortStudentsByPreferences();
        }
    }

    ngOnChanges() {
        setTimeout(() => {
            if (this.selectedStudent) {
                const student = this.students.filter(o => o.Id == this.selectedStudent.Id)[0];
                if (student) {
                    this.studentIndex = this.students.indexOf(student);
                }
                else {
                    this.studentIndex = 0;
                }
            }
        }, 100);
    }

    // Event Functions
    ngOnInit() {

        // Setting initial values
        if (!this.selectedStudent) {
            this.selectedStudent = this.students[this.studentIndex];
        }

        if (this.userType == 1) {
            this.generalPreferences = this.userPreferencesService.generalPreferences;
            // Sorting students to ensure preferences
            if (!this.overrideSort) {
                this.sortStudentsByPreferences();
            }
            if (!this.generalPreferences.ShowStudentNumber) {
                this.showNumber = false;
            }

            // Subscribing to preference changes to resort students
            this.subscriptions.push(
                this.userPreferencesService.observablegeneralPreferencesStream$.subscribe(preferences => {
                    this.generalPreferences = preferences;
                    if (!this.overrideSort) {
                        this.sortStudentsByPreferences();
                    }
                })
            );
        }

        // Responding to selected student changes unless we are overriding
        if (!this.overrideStudent) {
            this.subscriptions.push(
                this.studentService.selectedStudentStream$.subscribe(() => {
                    if (this.studentService.selectedStudent) {
                        const indexOfSelectedStudent = this.students.findIndex(student => student.Id == this.studentService.selectedStudent.Id);
                        if (indexOfSelectedStudent >= 0) {
                            this.studentIndex = indexOfSelectedStudent;
                            this.selectedStudent = this.students[indexOfSelectedStudent];
                        }
                    }
                })
            );
        }


        // Modifying options
        if (this.includeAllOption && this.students.filter(student => student.Id == -1).length == 0) {
            const allStudent = new Student;
            allStudent.Id = -1;
            this.students.unshift(allStudent);
            const indexOfSelectedStudent = this.students.findIndex(student => student.Id == this.selectedStudent.Id);
            if (indexOfSelectedStudent >= 0) {
                this.studentIndex = indexOfSelectedStudent;
                this.selectedStudent = this.students[indexOfSelectedStudent];
            }
        }

        this.userPreferencesService.observablegeneralPreferencesStream$.subscribe(preferences => {
            this.getStudentLabels();
        });
        this.getStudentLabels();
    }

    ngOnDestroy(): void {
        // POST: unsubscribing to all subscriptions
        this.subscriptions.forEach(subscription => {
            subscription.unsubscribe();
        });
    }

    getStudentLabels() {
        let studentSelectionName = [];
        for (const student of this.students) {
            let studentLabel = '';
            if (student.Id == -1 && this.userType == 1) {
                studentLabel = '(All)';
            }
            else if (student.Id > 0 && this.userType == 1) {
                if (this.userService.userType == 1) {
                    studentLabel = this.studentService.formatStudentName(student.FirstName, student.MiddleName, student.LastName, student.NickName);
                }
                else {
                    studentLabel = student.FirstName + ' ' + student.LastName;
                }
            }
            else if (student.Id > 0 && this.userType == 2) {
                studentLabel = student.FirstName + ' ' + student.LastName;
            }

            if (student.Id > 0 && this.showNumber) {
                studentLabel = studentLabel + ' (#' + student.StudentNumber + ')';
            }

            const suffix = this.getStudentSuffix(student);
            studentLabel = (suffix.length > 0) ? studentLabel + ' ' + suffix : studentLabel;
            student.studentSelectionName = studentLabel;
            if (student.WithdrawnDate != null) {
                const label = student.studentSelectionName + ' Withdrawn ' + moment(student.WithdrawnDate, 'YYYY/MM/DD').format('MM/DD/YY').trim();
                studentSelectionName.push(label);
            } else if (student.AddedOn != null) {
                const label = student.studentSelectionName + ' Added ' + moment(student.AddedOn, 'YYYY/MM/DD').format('MM/DD/YY').trim();
                studentSelectionName.push(label);
            }
            else {
                studentSelectionName.push(student.studentSelectionName);
            }

        }
        if (studentSelectionName.length > 0) {
            const longestStudentName = (this.longestStudentName(studentSelectionName)[0].trim()).length;
            if (longestStudentName != null) {
                // calculate: string convert into pixel
                this.studentdropDownWidth = parseInt(longestStudentName, 10) * 8;
            }
        }
    }

    firstStudent() {
        // PRE: the first student button is clicked
        // POST: navigates to the very first student
        this.previousSelectedStudent = JSON.parse(JSON.stringify(this.selectedStudent));
        this.studentIndex = 0;
        this.selectedStudent = this.students[this.studentIndex];
        this.changeStudent(this.selectedStudent);
    }

    previousStudent() {
        // PRE: the previous student button is clicked
        // POST: navigates to the previous student
        this.previousSelectedStudent = JSON.parse(JSON.stringify(this.selectedStudent));
        this.studentIndex = this.studentIndex - 1;
        this.selectedStudent = this.students[this.studentIndex];
        this.changeStudent(this.selectedStudent);
    }

    nextStudent() {
        // PRE: the next student button is clicked
        // POST: navigates to the next student
        this.previousSelectedStudent = JSON.parse(JSON.stringify(this.selectedStudent));
        this.studentIndex = this.studentIndex + 1;
        this.selectedStudent = this.students[this.studentIndex];
        this.changeStudent(this.selectedStudent);
    }

    lastStudent() {
        // PRE: the last student button is clicked
        // POST: navigates to the very last student
        this.previousSelectedStudent = JSON.parse(JSON.stringify(this.selectedStudent));
        this.studentIndex = this.students.length - 1;
        this.selectedStudent = this.students[this.studentIndex];
        this.changeStudent(this.selectedStudent);
    }

    changeStudent(student, dropDown?: boolean) {
        if (dropDown) {
            this.studentIndex = this.students.indexOf(student);
        }
        if (this.forStudentSearch && !this.overrideStudent) {
            this.studentService.updateSelectedStudent(student.Id);
        }
        this.studentChanged.emit(student.Id);
    }

    // Methods
    changeStudentById(studentId) {
        // POST: changes the student by the ID
        for (let i = 0; i < this.students.length; i++) {
            if (this.students[i].Id == studentId) {
                this.studentIndex = i;
            }
        }
        this.selectedStudent = this.students[this.studentIndex];
        // this.studentService.selectedStudent.Id = studentId;
        this.changeStudent(this.selectedStudent);
    }

    sortStudentsByPreferences() {
        // PRE: the general preferences are set
        // POST: sorts the students by the preferences

        // Returning if we have no preferences and logging it
        if (!this.generalPreferences) {
            console.log('No preferences');
            return;
        }

        // Performing sort
        switch (this.generalPreferences.StudentNameFormat.charAt(0)) {
            case 'F':
                this.students.sort((a: Student, b: Student) => {
                    const firstNameComparison = a.FirstName.localeCompare(b.FirstName);
                    if (firstNameComparison == 0) {
                        return a.LastName.localeCompare(b.LastName);
                    } else {
                        return firstNameComparison;
                    }
                });
                break;
            case 'L':
                this.students.sort((a: Student, b: Student) => {
                    const lastNameComparison = a.LastName.localeCompare(b.LastName);
                    if (lastNameComparison == 0) {
                        return a.FirstName.localeCompare(b.FirstName);
                    } else {
                        return lastNameComparison;
                    }
                });
                break;
            default:
                break;
        }
    }

    getStudentSuffix(student: Student): string {
        const suffix = student.Suffix;
        if (suffix != null && suffix.length > 0) {
            const numeralMatch = /^M{0,4}(CM|CD|D?C{0,3})(XC|XL|L?X{0,3})(IX|IV|V?I{0,3})$|^\d+$/;
            if (suffix.match(numeralMatch)) {
                return ' ' + suffix;
            }
            else {
                return ', ' + suffix;
            }
        }
        return '';
    }

    disableToggle(action) {
        // PRE: action is the name of the button
        // POST: determines if the button should be active or inactve
        if (this.students != undefined) {
            if ((action == 'first' || action == 'previous') && this.studentIndex == 0) {
                return true;
            }
            else if ((action == 'last' || action == 'next') && this.studentIndex == this.students.length - 1) {
                return true;
            }
            return false;
        }
    }
    // Dynamically Adjust Student dropDown List Width
    longestStudentName(student) {
        let max = student[0].length;
        student.map(v => max = Math.max(max, v.length));
        const result = student.filter(v => v.length == max);
        return result;
    }
    open() {
        this.dropdownlist.popupSettings.width = (this.studentdropDownWidth).toString() + 'px';
    }
}
